import React, { useEffect, useState } from "react";
import { Users, ShieldCheck, ClipboardCheck, Search, Crown, Check, UserCog } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5E2DC] bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-neutral-500"><Icon size={16} /><span className="text-sm font-medium">{label}</span></div>
      <p className="mt-2 font-head text-4xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = async (filter = tenantFilter) => {
    try {
      const url = filter ? `/reporting/overview?tenant=${filter}` : "/reporting/overview";
      const { data } = await client.get(url);
      setData(data);
      if (data.is_super && !tenants.length) {
        client.get("/admin/tenants").then(({ data }) => setTenants(data.tenants)).catch(() => {});
      }
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePro = async (m) => {
    setBusy(m.id + "-pro");
    try {
      await client.post(`/reporting/members/${m.id}/${m.is_pro ? "revoke" : "grant"}`);
      toast.success(m.is_pro ? `Revoked Pro for ${m.email}` : `Granted Pro to ${m.email}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(null); }
  };

  const toggleRole = async (m) => {
    const makePA = m.role !== "partner_admin";
    setBusy(m.id + "-role");
    try {
      await client.post(`/admin/members/${m.id}/role`, { role: makePA ? "partner_admin" : "member" });
      toast.success(makePA ? `${m.email} is now a partner admin` : `Removed partner-admin from ${m.email}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(null); }
  };

  if (forbidden) return <div className="mx-auto max-w-lg rounded-2xl border border-[#E5E2DC] bg-white p-10 text-center text-neutral-600" data-testid="admin-forbidden">This area is for administrators only.</div>;
  if (!data) return <div className="grid h-64 place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-[var(--tenant-primary)]" /></div>;

  const isSuper = data.is_super;
  const filtered = data.members.filter((m) => {
    const s = q.trim().toLowerCase();
    return !s || (m.email + " " + m.name + " " + m.business_name).toLowerCase().includes(s);
  });
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div className="fade-up space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">{isSuper ? "Admin" : "Partner dashboard"}</p>
        <h1 className="mt-2 font-head text-3xl font-semibold tracking-tight text-neutral-900">{isSuper ? "Members & Pro access" : "Your members"}</h1>
        <p className="mt-2 text-neutral-600">{isSuper ? "See engagement across all partners, or filter to one. Grant/revoke Pro and assign partner admins." : "Engagement and progress for your organization's members."}</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-3">
        <Stat icon={Users} label="Total members" value={data.stats.total} testid="stat-total" />
        <Stat icon={ShieldCheck} label="Pro members" value={data.stats.pro} testid="stat-pro" />
        <Stat icon={ClipboardCheck} label="Diagnostics completed" value={data.stats.diagnostics} testid="stat-diagnostics" />
      </div>

      <div className="rounded-2xl border border-[#E5E2DC] bg-white">
        <div className="flex flex-col gap-2 border-b border-[#E5E2DC] px-5 py-4 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2">
            <Search size={18} className="text-neutral-400" />
            <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or business…" className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-400" />
          </div>
          {isSuper && (
            <select data-testid="tenant-filter" value={tenantFilter} onChange={(e) => { setTenantFilter(e.target.value); load(e.target.value); }} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm outline-none">
              <option value="">All partners</option>
              {tenants.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
            </select>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-neutral-400">
              <tr className="border-b border-[#E5E2DC]">
                <th className="px-5 py-3 font-medium">Member</th>
                {isSuper && <th className="px-5 py-3 font-medium">Partner</th>}
                <th className="px-5 py-3 font-medium">Diagnostic</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-b border-[#F0EEE9] last:border-0" data-testid={`member-row-${m.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium text-neutral-900">{m.name || "—"}
                      {m.role === "admin" && <span className="ml-1 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-neutral-500">Admin</span>}
                      {m.role === "partner_admin" && <span className="ml-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tenant-text" style={{ backgroundColor: "var(--tenant-primary-light)" }}>Partner Admin</span>}
                    </div>
                    <div className="text-neutral-500">{m.email}{m.business_name ? ` · ${m.business_name}` : ""}</div>
                  </td>
                  {isSuper && <td className="px-5 py-4 text-neutral-500">{m.tenant_slug}</td>}
                  <td className="px-5 py-4">{m.completed_diagnostic ? <span className="inline-flex items-center gap-1 text-neutral-600"><Check size={14} className="tenant-text" /> Done</span> : <span className="text-neutral-400">Not yet</span>}</td>
                  <td className="px-5 py-4 text-neutral-500">{fmtDate(m.created_at)}</td>
                  <td className="px-5 py-4">
                    {m.is_pro ? <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold tenant-text" style={{ backgroundColor: "var(--tenant-primary-light)" }}><Crown size={12} /> Pro</span>
                      : <span className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-500">Free</span>}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {isSuper && m.role !== "admin" && (
                        <button data-testid={`toggle-role-${m.id}`} onClick={() => toggleRole(m)} disabled={busy === m.id + "-role"} title={m.role === "partner_admin" ? "Remove partner-admin" : "Make partner admin"}
                          className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-2 text-xs font-medium text-neutral-600 transition-all hover:bg-neutral-50 disabled:opacity-50">
                          <UserCog size={13} /> {m.role === "partner_admin" ? "Unset PA" : "Set PA"}
                        </button>
                      )}
                      <button data-testid={`toggle-pro-${m.id}`} onClick={() => togglePro(m)} disabled={busy === m.id + "-pro"}
                        className={`rounded-full px-4 py-2 text-xs font-semibold transition-all disabled:opacity-50 ${m.is_pro ? "border border-neutral-200 text-neutral-600 hover:bg-neutral-50" : "text-white hover:-translate-y-0.5"}`}
                        style={!m.is_pro ? { backgroundColor: "var(--tenant-primary)" } : {}}>
                        {busy === m.id + "-pro" ? "…" : m.is_pro ? "Revoke Pro" : "Grant Pro"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={isSuper ? 6 : 5} className="px-5 py-10 text-center text-neutral-400">No members found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
